import { useEffect, useRef, useState } from "react";
import { Typography, Button, Stack, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { deleteFileThunk, fetchFileModuleThunk, uploadFileThunk } from "../../../store/thunk/serviceModulesThunk";
import { FileServiceModule, ServiceModuleTypes } from "../../../types/serviceModuleTypes";
import { FileUploaderContainer } from "./FolderModule.styles";
import { fileToBase64 } from "../../../common/logic/fileToBase64";
import { GeneralButton } from "../../../common/ui/GeneralButton";

type FileUploaderProps = {
    serviceModuleId: number;
    file: File | null;
    onFileChange: (file: File | null) => void;
};

export default function FileUploader({ serviceModuleId, file, onFileChange }: FileUploaderProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isExistingFile, setIsExistingFile] = useState<boolean>(false);
    const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        let isMounted = true;
        const fetchExistingFile = async () => {
            try {
                const result = await dispatch(fetchFileModuleThunk(serviceModuleId)).unwrap() as FileServiceModule;
                if (isMounted && result.type === ServiceModuleTypes.File && result.downloadUrl) {
                    setIsExistingFile(true);
                    setExistingFileUrl(result.downloadUrl)
                }
            } catch (error) {
                if (isMounted) {
                    console.error("No existing file found or error fetching file", error);
                }
            }
        };
        fetchExistingFile();
        return () => { isMounted = false; };
    }, [dispatch, serviceModuleId]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFile = e.target.files?.[0] || null;
        if (!newFile) return;

        if (newFile.size === 0) {
            setFileError(t("serviceModulesPage.folderServicePage.fileEmpty"));
            setPreviewUrl(null);
            return;
        }

        if (newFile.size > 5 * 1024 * 1024) {
            setFileError(t("serviceModulesPage.folderServicePage.fileTooLarge"));
            setPreviewUrl(null);
            return;
        }

        onFileChange(newFile);
        setSelectedFile(newFile);
        setFileError(null);

        if (newFile.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(newFile);
        } else {
            setPreviewUrl(null);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        try {
            await dispatch(uploadFileThunk({
                serviceModuleId,
                file: selectedFile,
            })).unwrap();
            setIsExistingFile(true);
            setSelectedFile(null);
            setPreviewUrl(null);
            onFileChange(null);
        } catch (error) {
            console.error("Failed to upload file", error);
        }
    };

    const handleDelete = async () => {
        try {
            await dispatch(deleteFileThunk(serviceModuleId)).unwrap();
            setPreviewUrl(null);
            setSelectedFile(null);
            setIsExistingFile(false);
        } catch (error) {
            console.error("Failed to delete file", error);
        }
    };

    const triggerFileSelect = () => {
        fileInputRef.current?.click();
    };

    const isPdf = selectedFile?.type === "application/pdf";

    return (
        <FileUploaderContainer>
            <Stack spacing={2} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                    {t("buttons.uploadFile")}
                </Typography>

                {existingFileUrl && !previewUrl && (
                    <Stack spacing={1}>
                        <Typography variant="body2">Existing File:</Typography>
                        {existingFileUrl.endsWith(".pdf") ? (
                            <Button variant="text" href={existingFileUrl} target="_blank" rel="noopener noreferrer">
                                Open PDF
                            </Button>
                        ) : (
                            <Box
                                sx={{
                                    maxWidth: 500,
                                    maxHeight: 300,
                                    overflow: "hidden",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#f8f8f8",
                                    borderRadius: 1,
                                }}
                            >
                                <img
                                    src={existingFileUrl}
                                    alt="Uploaded"
                                    style={{
                                        width: "auto",
                                        height: "auto",
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain"
                                    }}
                                />
                            </Box>
                        )}
                    </Stack>
                )}

                <input
                    type="file"
                    accept="image/*,application/pdf"
                    ref={fileInputRef}
                    hidden
                    onChange={handleFileChange}
                />

                {!isExistingFile && (
                    <GeneralButton appVariant="secondary" onClick={triggerFileSelect}>
                        {t("buttons.selectFile")}
                    </GeneralButton>
                )}

                {fileError && (
                    <Typography color="error" variant="body2">
                        {fileError}
                    </Typography>
                )}

                {selectedFile && (
                    <Stack spacing={1} sx={{ alignItems: "center" }}>
                        {previewUrl ? (
                            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    style={{ maxWidth: "100%", height: "auto", borderRadius: 8 }}
                                />
                            </Box>
                        ) : isPdf ? (
                            <Typography variant="body2" sx={{ fontSize: "1.5rem", textAlign: "center" }}>
                                📄 {selectedFile.name}
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ textAlign: "center" }}>
                                {selectedFile.name}
                            </Typography>
                        )}
                        <GeneralButton
                            appVariant="primary"
                            disabled={!selectedFile}
                            onClick={handleUpload}
                        >
                            {t("buttons.uploadFile")}
                        </GeneralButton>
                    </Stack>
                )}

                {isExistingFile && (
                    <Stack direction="row" spacing={2} sx={{ justifyContent: "center" }}>
                        <GeneralButton appVariant="danger" onClick={handleDelete}>
                            {t("buttons.deleteFile")}
                        </GeneralButton>

                        <GeneralButton appVariant="secondary" onClick={triggerFileSelect}>
                            {t("buttons.replaceFile")}
                        </GeneralButton>
                    </Stack>
                )}
            </Stack>
        </FileUploaderContainer>
    );
}
